import { Col, Form, Row } from 'antd';
import type { FormInstance, RuleObject } from 'antd/es/form';
import type {
  CatalogPriceZoneSPPriceFamily,
  SelectedProductsAndSellingPrices,
} from 'hooks/catalog-price-zone-service-provider';
import { Fragment, JSX, useEffect, useMemo } from 'react';
import type { OrderPrestataireLine } from '.';
import { FormValues } from './PriceValueCell';
import { PriceType } from 'models';
import { PRICE_TYPES } from 'utils/constant';
import { handleCalculateUnitPrix } from './GenerateCatalogPriceSection';

interface MargeLineCellProps {
  form: FormInstance;
  value?: {
    uuid?: string;
    productId?: number;
    productName?: string;
    priceFamilies?: CatalogPriceZoneSPPriceFamily[];
    selectedPriceFamilyId?: number | null;
    priceMargin?: string;
  }[];
  record: OrderPrestataireLine;
  priceTypes: PriceType[];
  selectedProductsFromOrderClient: SelectedProductsAndSellingPrices[];
  updateDataSource?: (data: OrderPrestataireLine, serviceProviderId?: number) => void;
}

const MargeLineCell = (props: MargeLineCellProps) => {
  const {
    form,
    value,
    record,
    priceTypes,
    selectedProductsFromOrderClient,
    updateDataSource
  } = props;
  return (
    <section className='section'>
      {value?.map((item) => {
        const hasTarifSection = item?.priceFamilies && item.priceFamilies.length > 0;
        return (
          <div key={item?.uuid + '-' + item.productId}>
            <Row gutter={[16, 0]} align='middle' className='generate-price-value-cell margin-line-cell'>
              <MarginCalculation
                form={form}
                item={item}
                record={record}
                priceTypes={priceTypes}
                productListFromOrderClient={selectedProductsFromOrderClient}
                updateDataSource={updateDataSource}
              />
            </Row>
            {hasTarifSection && (
              <div className='generate-price-value-cell' style={{ width: '90px' }}>
                {generateMargeSpacingSection(item?.priceFamilies || [], item?.selectedPriceFamilyId)}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

/**
 * Calculates the margin between the client price and the service provider price.
 * @param {number} clientPrice - The price of the product for the client.
 * @param {number} spPrice - The price of the product for the service provider.
 * @returns {number} The margin between the client price and the service provider price, expressed as a percentage.
 */
const calculateMargin = (clientPrice: number, spPrice: number, isEnlevementTransport: boolean = false): string => {
  if (!clientPrice || clientPrice === 0) return '0';

  // Calculate margin
  let margin = ((clientPrice - spPrice) / clientPrice) * 100;

  // If enlevement transport, divide margin by 2
  if (isEnlevementTransport) {
    margin /= 2;
  }

  // Round margin to 2 decimal places
  return margin.toFixed(2);
};

const MarginCalculation = ({
  form,
  item,
  record,
  priceTypes,
  productListFromOrderClient,
  updateDataSource,
}: {
  form: FormInstance;
  item: {
    uuid?: string;
    productId?: number;
    productName?: string;
    priceFamilies?: CatalogPriceZoneSPPriceFamily[];
    selectedPriceFamilyId?: number | null;
    sellingUnitPrice?: number ;
    priceValue?: number | null;
    priceMargin?: string;
    documentProductLineId?: number;
  };
  record: OrderPrestataireLine;
  priceTypes: PriceType[];
  productListFromOrderClient: SelectedProductsAndSellingPrices[];
  updateDataSource?: (data: OrderPrestataireLine, serviceProviderId?: number) => void;
}) => {
  const formValuesWatch = Form.useWatch<FormValues>([], form);
  
  const selectedProductFromOrderClient = productListFromOrderClient.find((sp) => sp.productId === item.productId && sp.documentProductLineId === item.documentProductLineId);
  const qtyPriceType = priceTypes?.find((priceType) => priceType.key === PRICE_TYPES.numberInput);

  // Calculate margin
  const margin = useMemo(() => {
    const unitPriceFromOrderSP = handleCalculateUnitPrix({
      form,
      formValuesWatch,
      record,
      uuid: item?.uuid,
      productId: item?.productId,
      priceFamilies: item?.priceFamilies ?? [],
      selectedPriceFamilyId: item?.selectedPriceFamilyId,
      qtyPriceType,
    });
    let unitPriceFromOrderClient = selectedProductFromOrderClient?.sellingUnitPrice ?? 0;
    // if(item.productName && /Transport Benne : Rotation - .+/.test(item.productName)) {
    //   return calculateMargin(item.sellingUnitPrice ?? 0, item.priceValue ?? 0, true);
    // }
    // if (
    //   item.productName &&
    //   (/Transport Benne : Pose - .+/.test(item.productName) ||
    //     /Transport Benne : Enlèvement - .+/.test(item.productName) ||
    //     /Transport Benne : Rotation - .+/.test(item.productName))
    // ) {
    //   unitPriceFromOrderClient =
    //     productListFromOrderClient.find(
    //       (sp) => sp.productName && /Transport Benne : Pose et enlèvement - .+/.test(sp.productName),
    //     )?.sellingUnitPrice ?? 0;
    //   const isEnlevementTransport =
    //     /Transport Benne : Pose - .+/.test(item.productName) ||
    //     /Transport Benne : Enlèvement - .+/.test(item.productName)||
    //     /Transport Benne : Rotation - .+/.test(item.productName);
    //   return calculateMargin(unitPriceFromOrderClient, unitPriceFromOrderSP, isEnlevementTransport);
    // }
    return calculateMargin(unitPriceFromOrderClient, unitPriceFromOrderSP);
  }, [
    form,
    formValuesWatch,
    record,
    item?.uuid,
    item?.productId,
    item?.priceFamilies,
    item?.selectedPriceFamilyId,
    qtyPriceType,
    selectedProductFromOrderClient?.sellingUnitPrice,
  ]);

  useEffect(() => {
    const fieldPath = [
      'lineItems',
      `${record?.serviceProviderId}`,
      'products',
      `${item?.uuid}-${item?.productId}`,
      'marge',
    ];
    if (`${form.getFieldValue(fieldPath)}` !== `${margin}`) {
      form.setFieldsValue({
        lineItems: {
          [`${record?.serviceProviderId}`]: {
            products: {
              [`${item?.uuid}-${item?.productId}`]: {
                marge: margin,
              },
            },
          },
        },
      });
    }
    if (
      updateDataSource &&
      record.products.find(
        (product) =>
          product.uuid === item.uuid &&
          product.productId === item.productId &&
          product.priceMargin !== margin
      )
    ) {
      const updatedProducts = record.products.map((product) =>
        product.uuid === item.uuid && product.productId === item.productId
          ? { ...product, priceMargin: margin }
          : product
      );
      updateDataSource(
        {
          ...record,
          products: updatedProducts,
        },
        record.serviceProviderId,
      );
    }
  }, [form, record, item, margin, updateDataSource]);

  return (
    <Col className='marge-section' style={{ paddingLeft: 0, paddingRight: 0 }}>
      <Form.Item
        key={item.productId}
        name={['lineItems', `${record.serviceProviderId}`, 'products', `${item?.uuid}-${item.productId}`, 'marge']}
        rules={[
          { required: true, message: 'Marge is required' },
          () => ({
            validator(_: RuleObject, value) {
              if (isNaN(value) || Number.parseFloat(value) < 0) {
                return Promise.reject('Invalid marge');
              }
              return Promise.resolve();
            },
          }),
        ]}
        initialValue={margin}
      >
        <div style={{ color: Number.parseFloat(margin) < 0 ? '#FF0000' : '#52C41A' }}>{margin.replace(/\./g, ',')} %</div>
      </Form.Item>
    </Col>
  );
};

const generateMargeSpacingSection = (priceFamilies: CatalogPriceZoneSPPriceFamily[], priceFamilyId?: number | null) => {
  let content: JSX.Element = <></>;
  const selectedPriceFamily = priceFamilies.find((priceFamily) => priceFamily.id === priceFamilyId);

  if (priceFamilies.length > 0) {
    content = (
      <>
        <Row gutter={[16, 0]} className='type-du-tarif'>
          <Col xs={{ span: 18 }} style={{ textAlign: 'right', paddingRight: 0 }}>
            <Form.Item initialValue={priceFamilyId}></Form.Item>
          </Col>
        </Row>
        {priceFamilyId && (
          <Row gutter={[16, 0]}>
            <Col xs={{ span: 24 }}>
              <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 8 }} className='long-label-item'></Form.Item>
            </Col>
          </Row>
        )}
        {selectedPriceFamily?.prices?.map((tarif, indexTarif) => (
          <Fragment key={`${tarif?.id}-${indexTarif}`}>
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} id='info-tarif'></Col>
            </Row>
            {tarif?.priceOptions?.map((priceOption, indexPriceOption) => (
              <Fragment key={`${priceOption?.id}-${indexPriceOption}`}>
                <Row gutter={[16, 0]}>
                  <Col xs={{ span: 24 }}>
                    <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 8 }}></Form.Item>
                  </Col>
                </Row>
              </Fragment>
            ))}
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }}>
                <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 8 }}></Form.Item>
              </Col>
            </Row>
          </Fragment>
        ))}
      </>
    );
  } else {
    content = (
      <>
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} className='text-align-right'>
            <Form.Item></Form.Item>
          </Col>
        </Row>
      </>
    );
  }
  return <section className='section mt-2 mb-2 generate-tarif-section-empty-space'>{content}</section>;
};

export default MargeLineCell;
